import React, { useRef } from 'react';
import './Services.css';
import InfernoCanvas from './InfernoCanvas';

/* ─── Title ─────────────────────────────────────────────────── */
function CircleTitle({ name, label }) {
  return (
    <h3 className="circle-name">
      <span className="cn-base">{name}</span>
      <span className="cn-label">{label}</span>
    </h3>
  );
}

/* ─── SVG Arc Glyph ─────────────────────────────────────────── */
function ArcGlyph({ numeral, index }) {
  const r           = 82;
  const cx          = 100;
  const cy          = 100;
  const stroke      = 2 * Math.PI * r;
  const gapFrac     = 0.18 + (index % 4) * 0.025;
  const visibleDash = stroke * (1 - gapFrac);
  const gapDash     = stroke * gapFrac;
  const rotOffset   = -80 + (index * 11) % 60;

  return (
    <svg className="arc-glyph" viewBox="0 0 200 200" aria-hidden="true">
      <circle cx={cx} cy={cy} r={r}      fill="none" stroke="rgba(139,0,0,0.12)" strokeWidth="1" />
      <circle cx={cx} cy={cy} r={r + 10} fill="none" stroke="rgba(201,168,76,0.06)" strokeWidth="1" strokeDasharray="4 8" />
      <circle
        cx={cx} cy={cy} r={r}
        fill="none" stroke="url(#arcGrad)" strokeWidth="2.5" strokeLinecap="round"
        strokeDasharray={`${visibleDash} ${gapDash}`}
        transform={`rotate(${rotOffset} ${cx} ${cy})`}
      />
      <circle
        cx={cx} cy={cy} r={r - 9}
        fill="none" stroke="rgba(139,0,0,0.35)" strokeWidth="1" strokeLinecap="round"
        strokeDasharray={`${stroke*(1-gapFrac-0.05)} ${stroke*(gapFrac+0.05)}`}
        transform={`rotate(${rotOffset+4} ${cx} ${cy})`}
      />
      {[rotOffset, rotOffset + (1 - gapFrac) * 360].map((ang, di) => {
        const rad = ang * Math.PI / 180;
        return (
          <circle key={di}
            cx={cx + r * Math.cos(rad)}
            cy={cy + r * Math.sin(rad)}
            r="3" fill="var(--crimson)" opacity="0.7"
          />
        );
      })}
      <text x={cx} y={cy + 18} textAnchor="middle" className="arc-numeral-text">{numeral}</text>
      <defs>
        <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%"   stopColor="#c9a84c" stopOpacity="0.9" />
          <stop offset="60%"  stopColor="#8b0000" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#c9a84c" stopOpacity="0.5" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* ─── Main export ───────────────────────────────────────────── */
export default function Services({ t }) {
  const sectionRef = useRef(null);
  return (
    <section id="services" className="services" ref={sectionRef}>
      <InfernoCanvas sectionRef={sectionRef} />
      <div className="services-header">
        <h2 className="section-title">{t.title}</h2>
        <p className="section-subtitle">{t.subtitle}</p>
      </div>

      <div className="circles-list">
        {t.items.map((item, i) => (
          <div key={i} id={`circle-${i + 1}`} className="circle-row">
            <div className="circle-glyph-col">
              <ArcGlyph numeral={item.numeral} index={i} />
            </div>
            <div className="circle-text-col">
              <CircleTitle name={item.name} numeral={item.numeral} label={item.label} />
              <p className="circle-dante">{item.dante}</p>
              <p className="circle-practical">{item.practical}</p>
            </div>
            {i < t.items.length - 1 && <div className="circle-divider" />}
          </div>
        ))}
      </div>
    </section>
  );
}
